import React from 'react';
import Select from 'react-select';
import styled, { useTheme } from 'styled-components';
import StyledColoredTag from '../styledComponents/StyledColoredTag';
import StyledSelectCss from '../styledComponents/StyledSelectCss';
import defaultTheme from '../../../utils/themes';

const mergeSelectStyles = (baseStyles, overrideStyles = {}) => {
  const keys = new Set([
    ...Object.keys(baseStyles),
    ...Object.keys(overrideStyles),
  ]);
  const merged = {};
  keys.forEach((key) => {
    merged[key] = (provided, state) => {
      const base = baseStyles[key]
        ? baseStyles[key](provided, state)
        : provided;
      return overrideStyles[key] ? overrideStyles[key](base, state) : base;
    };
  });
  return merged;
};

const StyledSelect = ({
  isMulti,
  options = [],
  value = '',
  placement,
  onChange,
  disabled = false,
  className = '',
  type = '',
  icon,
  styles: stylesProp,
  ...props
}) => {
  const themes = useTheme();
  const formSurface = themes?.cardSurface;
  const isDark = Boolean(themes?.dark);
  const menuBg = formSurface
    || (isDark
      ? themes.surface || themes.selectBg || '#28303A'
      : themes?.selectBg || themes?.white);
  const optionText = formSurface
    ? themes.headingColor
    : isDark
      ? themes.headingColor || '#fff'
      : themes.textColor;
  let newValue = null;
  if (value && !isMulti) {
    newValue = {
      value,
      label: options.find((o) => o.value === value)?.label || value,
    };
  } else newValue = value;
  const customStyles = {
    option: (provided, state) => ({
      ...provided,
      backgroundColor: formSurface
        ? 'transparent'
        : state.isSelected || state.isFocused
          ? isDark
            ? themes.fieldLabelBg || '#343B45'
            : themes.primaryLight
          : isDark
            ? 'transparent'
            : provided.backgroundColor,
      borderRadius: props.borderRadius ? props.borderRadius ?? 10 : 8,
      padding: '10px 15px',
      color: optionText,
      cursor: 'pointer',
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: menuBg,
      border: formSurface
        ? `1px solid ${themes.inputBorder || 'transparent'}`
        : isDark
          ? '1px solid rgba(255, 255, 255, 0.16)'
          : undefined,
      borderRadius: 12,
      boxShadow: themes?.shadowMd || '0 4px 16px rgba(0, 0, 0, 0.15)',
      overflow: 'hidden',
      zIndex: 20000,
    }),
    menuList: (provided) => ({
      ...provided,
      backgroundColor: formSurface || menuBg,
      padding: 0,
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 20000,
    }),
  };
  const optionalData = (option) => {
    return type === 'iconLabel' ? (
      <div className="d-flex gap-2 align-items-center">
        {option?.label?.icon} <label>{option?.label?.text}</label>
      </div>
    ) : (
      <div className="d-flex justify-content-between">
        <label>{option?.label}</label>
        {option?.data && (
          <StyledColoredTag variant={option?.color}>
            {option?.data}
          </StyledColoredTag>
        )}
      </div>
    );
  };

  return (
    <CustomSelect
      className={icon && icon ? 'analytics' : ''}
      publicForm={props.publicForm}
    >
      {icon && icon}
      <StyledSelectCss>
        <Select
          isMulti={isMulti}
          menuPlacement={placement ? placement : 'auto'}
          menuPortalTarget={
            typeof document !== 'undefined' ? document.body : null
          }
          menuPosition="fixed"
          isDisabled={disabled}
          options={options?.map((option) => option)}
          getOptionLabel={(option) => optionalData(option)}
          getOptionValue={(option) => option?.label}
          styles={mergeSelectStyles(customStyles, stylesProp)}
          theme={(theme) => ({
            ...theme,
            borderRadius: props.borderRadius || 10,
            colors: {
              ...theme.colors,
              neutral0: formSurface
                ? formSurface
                : isDark && !props.publicForm
                  ? menuBg
                  : theme.colors.neutral0,
              neutral5: formSurface
                ? 'transparent'
                : isDark && !props.publicForm
                  ? themes.fieldLabelBg || '#343B45'
                  : theme.colors.neutral5,
              neutral80: formSurface || isDark ? optionText : theme.colors.neutral80,
              primary25: formSurface
                ? 'transparent'
                : isDark && !props.publicForm
                  ? themes.fieldLabelBg || '#343B45'
                  : themes.lightHover,
              primary50: themes.primaryLight,
            },
          })}
          value={newValue}
          onChange={onChange}
          className={`async-select ${className} 
          ${props.isValid ? 'is-valid' : ''}
          ${props.isLightPink ? 'is-light-pink' : ''}
          ${props.pay ? ' is-light-pink pay' : ''}
        ${props.application ? 'application' : ''}
        ${props.workFlow ? 'workFlow' : ''}
        ${props.publicForm ? 'publicForm' : ''}
        ${props.analytics ? 'analytics' : ''}
         ${props.isInvalid ? 'is-invalid' : ''} 
         ${disabled ? 'disabled' : ''}`}
          {...props}
        />
      </StyledSelectCss>
    </CustomSelect>
  );
};

const CustomSelect = styled.div`
  &.analytics {
    > div {
      width: 100%;
    }
    display: flex;
    align-items: center;
    cursor: pointer;
    background: ${({ publicForm, theme }) =>
      !publicForm
        ? theme?.darkThemBoxBgColor || defaultTheme.white
        : defaultTheme.white};
    font-size: 14px;
    border: 1px solid ${({ theme }) => theme.borderLight};
    border-radius: ${({ radius }) => (radius ? radius : '8px')};
    width: 170px;
    margin-top: 5px;
    max-width: 170px;
    height: 34px;
    padding-block: 0px;
    &:hover,
    &:active,
    &:focus {
      border: 1px solid ${({ theme }) => theme.inputBorder};
      background: ${({ theme, publicForm }) =>
        theme?.dark && !publicForm
          ? theme?.primary
          : defaultTheme.primaryLight};
      color: ${({ theme }) => theme?.primary};
      box-shadow: none;
    }
  }
`;

export default StyledSelect;
